const DAY_MS = 24 * 60 * 60 * 1000

/** Valid sort orders for the plans inbox. */
export const PLAN_SORT_ORDERS = ['newest', 'oldest']

/** Age-filter presets (in days) offered by the Plans page. `0` means "any age". */
export const PLAN_AGE_FILTER_DAYS = [0, 1, 4, 7, 14, 30]

function planTimestamp(plan) {
  const value = Date.parse(plan?.jobCreatedAt ?? '')
  return Number.isNaN(value) ? null : value
}

/**
 * Filter pending plans by minimum age and sort by their related-run
 * timestamp. Pure and time-injectable so it can be unit-tested without a
 * clock or a mounted component.
 *
 * - `olderThanDays > 0` keeps only plans whose timestamp is at least that
 *   many days in the past. Plans with a missing/invalid timestamp are
 *   EXCLUDED when a filter is active — their age cannot be confirmed, so they
 *   must not be swept into an "older than N days" bulk action by accident.
 * - Sorting is by timestamp: `newest` descending, `oldest` ascending. Plans
 *   with a missing timestamp always sink to the bottom in either order so an
 *   unknown-age plan never masquerades as the oldest item.
 *
 * @param {Array<{ jobCreatedAt?: string }>} plans
 * @param {{ olderThanDays?: number, sortOrder?: 'newest'|'oldest' }} [options]
 * @param {number} [now]
 */
export function filterAndSortPendingPlans(plans, { olderThanDays = 0, sortOrder = 'newest' } = {}, now = Date.now()) {
  const list = Array.isArray(plans) ? plans : []
  const days = Number(olderThanDays) > 0 ? Number(olderThanDays) : 0

  const filtered = days > 0
    ? list.filter((plan) => {
      const ts = planTimestamp(plan)
      if (ts === null) return false
      return now - ts >= days * DAY_MS
    })
    : [...list]

  const direction = sortOrder === 'oldest' ? 1 : -1

  return filtered.sort((a, b) => {
    const aTs = planTimestamp(a)
    const bTs = planTimestamp(b)
    if (aTs === null && bTs === null) return 0
    if (aTs === null) return 1
    if (bTs === null) return -1
    return (aTs - bTs) * direction
  })
}
